import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Fingerprint, Loader2, AlertCircle } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, loginWithPasskey, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) {
      navigate('/app');
    }
  };

  const handlePasskeyLogin = async () => {
    const success = await loginWithPasskey();
    if (success) {
      navigate('/app');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FAF8F5] via-[#F5F0EB] to-[#EDE5DC] dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <div className="w-full max-w-md px-6">
        <div className="bg-white/80 dark:bg-sky-900/30 rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-2">
            <img src="/logos/froma-original.png" alt="Froma" className="h-12 w-auto dark:brightness-0 dark:invert" />
          </div>
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm mb-8">
            Froma Management System
          </p>

          {error && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearError();
                }}
                required
                aria-required="true"
                autoComplete="username"
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-sky-700/40 bg-white dark:bg-sky-900/50 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-[#B8860B] focus:border-transparent transition-shadow"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearError();
                }}
                required
                aria-required="true"
                autoComplete="current-password"
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-sky-700/40 bg-white dark:bg-sky-900/50 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-[#B8860B] focus:border-transparent transition-shadow"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-[#B8860B] text-white rounded-lg font-medium hover:bg-[#9A7209] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#B8860B]/25"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-sky-700/40" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-4 bg-white/80 dark:bg-sky-900/30 text-gray-400 tracking-wider">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={isLoading}
            className="w-full py-2.5 bg-white dark:bg-sky-900/50 text-gray-900 dark:text-white border border-[#B8860B]/30 dark:border-[#B8860B]/50 rounded-lg font-medium hover:border-[#B8860B] hover:bg-[#B8860B]/5 dark:hover:bg-[#B8860B]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Fingerprint className="w-5 h-5" />
            Sign in with Passkey
          </button>

          {!import.meta.env.DEV && (
            <a
              href="/"
              className="block text-center mt-6 text-sm text-gray-500 dark:text-gray-400 hover:text-[#B8860B] transition-colors"
            >
              &larr; Back to website
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
